import { useId, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { BarChart3 } from 'lucide-react';
import { Bar, BarChart, CartesianGrid, Cell, XAxis, YAxis } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ChartContainer, ChartTooltip } from '@/components/ui/chart';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { formatCents } from '@/lib/format/currency';
import {
  MONTHLY_EARNINGS_FETCH_MONTHS,
  MONTHLY_EARNINGS_RANGES,
  buildMonthlyEarningsChart,
  earningsAxisTicks,
  formatCompactCents,
  type MonthlyEarningsBar,
  type MonthlyEarningsRange,
  type MyWalletMonthlyEarningsData,
  type MyWalletMonthlyEarningsVars,
} from '@/lib/wallet/monthlyEarnings';
import { MY_WALLET_MONTHLY_EARNINGS } from '@/queries/walletMonthlyEarnings';

// emerald-500, the wallet page's accent. The current month is still in
// progress, so it is hatched (not faded, which would drop below 3:1 contrast).
const BAR_COLOR = '#10b981';

const EarningsTooltip = ({
  active,
  payload,
  currency,
}: {
  active?: boolean;
  payload?: { payload?: MonthlyEarningsBar }[];
  currency: string;
}) => {
  const { t } = useTranslation();
  const bar = payload?.[0]?.payload;
  if (!active || !bar) return null;
  const row = (label: string, cents: number) => (
    <div key={label} className="flex items-center justify-between gap-4">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-mono font-medium tabular-nums text-foreground">{formatCents(cents, currency)}</span>
    </div>
  );
  return (
    <div className="grid min-w-[11rem] gap-1.5 rounded-lg border border-border/50 bg-background px-2.5 py-1.5 text-xs text-foreground shadow-xl">
      <p className="font-medium">
        {bar.fullLabel}
        {bar.current ? ` · ${t('walletMonthlyEarnings.current')}` : ''}
      </p>
      {row(t('walletMonthlyEarnings.total'), bar.total_cents)}
      {bar.sources.map((source) => row(t(`walletMonthlyEarnings.sources.${source.key}`), source.cents))}
    </div>
  );
};

export const MonthlyEarningsChart = () => {
  const { t, i18n } = useTranslation();
  const [range, setRange] = useState<MonthlyEarningsRange>(6);
  const patternId = `wallet-earnings-current-${useId().replace(/:/g, '')}`;
  const { data, loading, error } = useQuery<MyWalletMonthlyEarningsData, MyWalletMonthlyEarningsVars>(
    MY_WALLET_MONTHLY_EARNINGS,
    { variables: { months: MONTHLY_EARNINGS_FETCH_MONTHS } },
  );
  const earnings = data?.myWalletMonthlyEarnings;
  const chart = useMemo(
    () => (earnings ? buildMonthlyEarningsChart({ months: earnings.months, range, locale: i18n.language }) : null),
    [earnings, range, i18n.language],
  );

  if (loading && !earnings) {
    return <Skeleton data-testid="wallet-monthly-earnings-loading" className="h-[19rem] rounded-xl" />;
  }
  // An older backend has no such query: the page simply has no chart.
  if (error || !earnings || !chart) return null;

  const currency = earnings.currency;
  const axisTicks = earningsAxisTicks(Math.max(0, ...chart.bars.map((bar) => bar.total_cents)));

  return (
    <Card>
      <CardHeader className="space-y-1 pb-2">
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2">
            <BarChart3 className="h-4 w-4 shrink-0 text-muted-foreground" />
            <CardTitle className="text-base">{t('walletMonthlyEarnings.title')}</CardTitle>
          </div>
          <Select value={String(range)} onValueChange={(value) => setRange(Number(value) as MonthlyEarningsRange)}>
            <SelectTrigger className="h-8 w-36 shrink-0 text-xs" aria-label={t('walletMonthlyEarnings.range.label')}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MONTHLY_EARNINGS_RANGES.map((months) => (
                <SelectItem key={months} value={String(months)}>
                  {t(`walletMonthlyEarnings.range.last${months}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {chart.hasEarnings && (
          <p className="text-xs text-muted-foreground">
            {t('walletMonthlyEarnings.periodTotal', { amount: formatCents(chart.totalCents, currency) })}
            {chart.bestMonth && (
              <>
                {' · '}
                {t('walletMonthlyEarnings.bestMonth', {
                  month: chart.bestMonth.fullLabel,
                  amount: formatCents(chart.bestMonth.total_cents, currency),
                })}
              </>
            )}
          </p>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        {chart.hasEarnings ? (
          <>
            <div aria-hidden="true">
              <ChartContainer
                config={{ total_cents: { label: t('walletMonthlyEarnings.total'), color: BAR_COLOR } }}
                className="aspect-auto h-[200px] w-full"
              >
                <BarChart data={chart.bars} margin={{ top: 8, right: 4, left: 0, bottom: 0 }}>
                  <defs>
                    <pattern id={patternId} width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                      <rect width="6" height="6" fill={BAR_COLOR} fillOpacity={0.4} />
                      <line x1="0" y1="0" x2="0" y2="6" stroke={BAR_COLOR} strokeWidth="3" />
                    </pattern>
                  </defs>
                  <CartesianGrid vertical={false} stroke="hsl(var(--border))" />
                  <XAxis
                    dataKey="label"
                    tickLine={false}
                    axisLine={false}
                    tickMargin={8}
                    interval={range === 6 ? 0 : 'preserveStartEnd'}
                    minTickGap={2}
                  />
                  <YAxis
                    tickLine={false}
                    axisLine={false}
                    width={68}
                    ticks={axisTicks}
                    domain={[0, axisTicks[axisTicks.length - 1]]}
                    interval={0}
                    tickFormatter={(value: number) => formatCompactCents(value, currency)}
                  />
                  <ChartTooltip
                    cursor={{ fill: 'hsl(var(--muted))', opacity: 0.4 }}
                    content={<EarningsTooltip currency={currency} />}
                  />
                  <Bar dataKey="total_cents" radius={[4, 4, 0, 0]} maxBarSize={24} isAnimationActive={false}>
                    {chart.bars.map((bar) => (
                      <Cell key={bar.month} fill={bar.current ? `url(#${patternId})` : BAR_COLOR} />
                    ))}
                  </Bar>
                </BarChart>
              </ChartContainer>
            </div>
            <table className="sr-only">
              <caption>{t('walletMonthlyEarnings.tableCaption')}</caption>
              <tbody>
                {chart.bars.map((bar) => (
                  <tr key={bar.month} data-testid="wallet-monthly-earnings-row">
                    <th scope="row">
                      {bar.fullLabel}
                      {bar.current ? ` (${t('walletMonthlyEarnings.current')})` : ''}
                    </th>
                    <td>{formatCents(bar.total_cents, currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-xs text-muted-foreground">{t('walletMonthlyEarnings.footnote')}</p>
          </>
        ) : (
          <p className="py-2 text-sm text-muted-foreground">{t('walletMonthlyEarnings.empty')}</p>
        )}
      </CardContent>
    </Card>
  );
};

export default MonthlyEarningsChart;
